import React, { useCallback, useRef } from "react";

import { Box, LinearProgress, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsTreemap from "highcharts/modules/treemap";
import { useCardSize } from "components/Report-generator/context/CardSizeContext";
import type { ExposureCardConfig, ExposureCardData, ExposureDimension, ExposureMeasure } from "store/types";

HighchartsTreemap(Highcharts);

// Match the Snapshot chart palette (src/components/reports/Chart/useChartHook.ts `graphColors`).
const COLORS = [
  "#4690F5",
  "#32CED8",
  "#D428BD",
  "#496682",
  "#6E45EB",
  "#EBAF01",
  "#D93E3E",
  "#D77108",
  "#0ABB91",
  "#FEA458"
];

const DIMENSION_LABELS: Record<ExposureDimension, string> = {
  assetClass: "Asset Class",
  Geography: "Geography",
  Strategy: "Strategy",
  Vintage: "Vintage"
};

const MEASURE_LABELS: Record<ExposureMeasure, string> = {
  exposure: "Total Exposure",
  adjNav: "Adjusted NAV",
  unfundedCommitment: "Unfunded Commitment"
};

const fmtValue = (v: number, postfix?: string) => {
  if (postfix === "%" || postfix === "pct") return `${v.toFixed(1)}%`;
  return `${postfix ?? "$"}${(v / 1_000_000).toFixed(1)}M`;
};
const fmtPct = (v: number) => `${v.toFixed(1)}%`;
const truncate = (s: string, max: number) => (s.length > max ? s.slice(0, max - 1) + "\u2026" : s);

interface Props {
  config: ExposureCardConfig;
  data?: ExposureCardData;
  loading?: boolean;
}

export const ExposureBreakdownCard: React.FC<Props> = ({ config, data, loading }) => {
  const { isEditMode, widthPercent } = useCardSize();
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  // Reflow the pie on card resize so it fills and scales with the card, matching ChartCard.
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      observerRef.current = new ResizeObserver(() => {
        chartRef.current?.chart?.reflow();
      });
      observerRef.current.observe(node);
    }
  }, []);

  const { dimension, chartType, measure } = config;
  const dimensionLabel = DIMENSION_LABELS[dimension];
  const measureLabel = MEASURE_LABELS[measure ?? "exposure"];

  const entries: ChartEntry[] = data
    ? data.map((d) => ({ name: d.label, value: d.value, pct: d.percentage, postfix: d.postfix }))
    : [];

  const headerText = `${measureLabel} by ${dimensionLabel}`;

  const header = (
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 600,
        color: "#6B7280",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        pl: "12px",
        mb: "4px"
      }}
    >
      {headerText}
    </Typography>
  );

  if (loading) {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        {header}
        <LinearProgress sx={{ mt: 4, mx: 2 }} />
      </Box>
    );
  }

  if (entries.length === 0) {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        {header}
        <Typography sx={{ fontSize: 11, color: "#9CA3AF", p: 2 }}>No data available</Typography>
      </Box>
    );
  }

  if (chartType === "donut" || chartType === "pie") {
    const donutOptions = buildDonutOptions({
      entries,
      dimensionLabel,
      innerSize: chartType === "donut" ? "45%" : "0%",
      isEditMode,
      widthPercent
    });
    return (
      <Box ref={containerRef} sx={{ p: "16px 8px 8px", height: "100%", display: "flex", flexDirection: "column" }}>
        {header}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <HighchartsReact
            ref={chartRef}
            key={chartType}
            highcharts={Highcharts}
            options={withoutAnimation(donutOptions)}
            containerProps={{ style: { height: "100%" } }}
          />
        </Box>
      </Box>
    );
  }

  if (chartType === "bar") {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        {header}
        <HorizontalBarChart entries={entries} dimensionLabel={dimensionLabel} isEditMode={isEditMode} />
      </Box>
    );
  }

  if (chartType === "column") {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        {header}
        <VerticalBarChart entries={entries} dimensionLabel={dimensionLabel} isEditMode={isEditMode} />
      </Box>
    );
  }

  if (chartType === "treemap") {
    return (
      <Box sx={{ p: "16px 8px 8px", height: "100%", display: "flex", flexDirection: "column" }}>
        {header}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <TreemapChart entries={entries} dimensionLabel={dimensionLabel} isEditMode={isEditMode} />
        </Box>
      </Box>
    );
  }

  // Fallback
  return (
    <Box sx={{ p: "16px 8px 8px" }}>
      {header}
      <Typography sx={{ fontSize: 11, color: "#9CA3AF", p: 2 }}>Unsupported chart type</Typography>
    </Box>
  );
};

// ─── Chart entry type ────────────────────────────────────────────────────────

interface ChartEntry {
  name: string;
  value: number;
  pct: number;
  postfix: string;
}

// ─── Reflowing chart wrapper ─────────────────────────────────────────────────
// Reflows the chart whenever its container resizes (e.g. card drag-resize), so
// every Exposure chart type scales with the card — mirroring ChartCard.

// Disable Highcharts entrance animation for report cards: the background PDF render (html2canvas)
// captures the DOM before an animated donut/series finishes drawing, producing half-rendered charts.
const withoutAnimation = (options: Highcharts.Options): Highcharts.Options => ({
  ...options,
  chart: { ...options.chart, animation: false },
  plotOptions: {
    ...options.plotOptions,
    series: { ...options.plotOptions?.series, animation: false }
  }
});

interface ReflowingChartProps {
  options: Highcharts.Options;
  fillHeight?: boolean;
}

const ReflowingChart: React.FC<ReflowingChartProps> = ({ options, fillHeight }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      observerRef.current = new ResizeObserver(() => {
        chartRef.current?.chart?.reflow();
      });
      observerRef.current.observe(node);
    }
  }, []);

  return (
    <div ref={containerRef} style={fillHeight ? { height: "100%" } : undefined}>
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={withoutAnimation(options)}
        containerProps={fillHeight ? { style: { height: "100%" } } : undefined}
      />
    </div>
  );
};

// ─── Donut / Pie ─────────────────────────────────────────────────────────────

interface DonutOptionsArgs {
  entries: ChartEntry[];
  dimensionLabel: string;
  innerSize: string;
  isEditMode: boolean;
  widthPercent: number;
}

const buildDonutOptions = ({ entries, dimensionLabel, innerSize, isEditMode, widthPercent }: DonutOptionsArgs): Highcharts.Options => {
  const chartData = entries.map((e, i) => ({
    name: e.name,
    y: e.value,
    color: COLORS[i % COLORS.length]
  }));
  // Scale fonts to the card width, matching ChartCard.
  const labelFont = widthPercent <= 34 ? "8px" : widthPercent <= 50 ? "9px" : "11px";

  return {
    chart: {
      type: "pie",
      height: null,
      marginTop: 8,
      style: { fontFamily: "inherit" }
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: isEditMode },
    tooltip: {
      pointFormatter() {
        const entry = entries.find((e) => e.name === this.name);
        return `<b>${fmtValue(this.y ?? 0, entry?.postfix)}</b> (${fmtPct(entry?.pct ?? 0)})`;
      }
    },
    plotOptions: {
      pie: {
        innerSize,
        dataLabels: {
          enabled: true,
          distance: 4,
          style: { fontSize: labelFont, fontWeight: "600", color: "#374151", textOutline: "none" },
          formatter() {
            const entry = entries.find((e) => e.name === this.point.name);
            return fmtPct(entry?.pct ?? 0);
          }
        },
        showInLegend: true
      }
    },
    // Bottom horizontal legend that wraps into multiple columns, matching ChartCard.
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
      itemStyle: { fontSize: labelFont, fontWeight: "normal", color: "#374151" },
      symbolRadius: 4,
      margin: 8,
      itemDistance: widthPercent <= 34 ? 6 : 12,
      labelFormatter() {
        const maxLen = widthPercent <= 34 ? 10 : 14;
        return truncate(this.name, maxLen);
      }
    },
    series: [
      {
        type: "pie",
        name: dimensionLabel,
        data: chartData
      }
    ]
  };
};

// ─── Horizontal Bar ──────────────────────────────────────────────────────────

interface BarChartProps {
  entries: ChartEntry[];
  dimensionLabel: string;
  isEditMode: boolean;
}

const HorizontalBarChart: React.FC<BarChartProps> = ({ entries, dimensionLabel, isEditMode }) => {
  const maxLabelLen = Math.max(...entries.map((e) => e.name.length), 0);
  const dynamicMarginLeft = Math.min(Math.max(maxLabelLen * 7, 60), 120);

  const options: Highcharts.Options = {
    chart: {
      type: "bar",
      height: Math.max(entries.length * 36 + 60, 180),
      marginTop: 12,
      marginRight: 16,
      marginLeft: dynamicMarginLeft,
      marginBottom: 24,
      style: { fontFamily: "inherit" }
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: isEditMode },
    xAxis: {
      categories: entries.map((e) => e.name),
      labels: {
        style: { fontSize: "11px", color: "#374151", fontWeight: "600" },
        formatter() {
          const v = String(this.value);
          return v.length > 14 ? v.slice(0, 13) + "\u2026" : v;
        }
      },
      lineColor: "#E5E7EB",
      tickColor: "transparent"
    },
    yAxis: {
      title: { text: null },
      labels: {
        formatter() {
          return fmtPct(this.value as number);
        },
        style: { fontSize: "10px", color: "#9CA3AF" }
      },
      max: 100,
      gridLineColor: "#F3F4F6"
    },
    tooltip: {
      formatter() {
        const entry = entries.find((e) => e.name === this.x);
        return `<b>${this.x}</b><br/>${fmtPct(this.y ?? 0)} (${fmtValue(entry?.value ?? 0, entry?.postfix)})`;
      }
    },
    plotOptions: {
      bar: {
        borderWidth: 0,
        borderRadius: 2,
        pointWidth: 16,
        colorByPoint: true,
        colors: COLORS,
        dataLabels: {
          enabled: true,
          formatter() {
            return fmtPct(this.y ?? 0);
          },
          style: { fontSize: "10px", fontWeight: "600", color: "#374151", textOutline: "none" }
        }
      }
    },
    legend: { enabled: false },
    series: [
      {
        type: "bar",
        name: dimensionLabel,
        data: entries.map((e) => e.pct)
      }
    ]
  };

  return <ReflowingChart options={options} />;
};

// ─── Vertical Bar (Column) ───────────────────────────────────────────────────

const VerticalBarChart: React.FC<BarChartProps> = ({ entries, dimensionLabel, isEditMode }) => {
  const options: Highcharts.Options = {
    chart: {
      type: "column",
      height: 260,
      marginTop: 12,
      style: { fontFamily: "inherit" }
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: isEditMode },
    xAxis: {
      categories: entries.map((e) => e.name),
      labels: {
        rotation: entries.length > 4 ? -45 : 0,
        style: { fontSize: "11px", color: "#374151", fontWeight: "600" },
        formatter() {
          const v = String(this.value);
          return v.length > 12 ? v.slice(0, 11) + "\u2026" : v;
        }
      },
      lineColor: "#E5E7EB",
      tickColor: "transparent"
    },
    yAxis: {
      title: { text: null },
      labels: {
        formatter() {
          return fmtPct(this.value as number);
        },
        style: { fontSize: "10px", color: "#9CA3AF" }
      },
      max: 100,
      gridLineColor: "#F3F4F6"
    },
    tooltip: {
      formatter() {
        const entry = entries.find((e) => e.name === this.x);
        return `<b>${this.x}</b><br/>${fmtPct(this.y ?? 0)} (${fmtValue(entry?.value ?? 0, entry?.postfix)})`;
      }
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        borderRadius: 2,
        colorByPoint: true,
        colors: COLORS,
        dataLabels: {
          enabled: true,
          formatter() {
            return fmtPct(this.y ?? 0);
          },
          style: { fontSize: "10px", fontWeight: "600", color: "#374151", textOutline: "none" }
        }
      }
    },
    legend: { enabled: false },
    series: [
      {
        type: "column",
        name: dimensionLabel,
        data: entries.map((e) => e.pct)
      }
    ]
  };

  return <ReflowingChart options={options} />;
};

// ─── Treemap ─────────────────────────────────────────────────────────────────

const TreemapChart: React.FC<BarChartProps> = ({ entries, dimensionLabel, isEditMode }) => {
  const treemapData = entries.map((e, i) => ({
    name: e.name,
    value: e.value,
    color: COLORS[i % COLORS.length]
  }));

  const options: Highcharts.Options = {
    chart: {
      height: null,
      style: { fontFamily: "inherit" }
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: isEditMode },
    tooltip: {
      formatter() {
        const entry = entries.find((e) => e.name === this.key);
        return `<b>${this.key}</b><br/>${fmtPct(entry?.pct ?? 0)} (${fmtValue(entry?.value ?? 0, entry?.postfix)})`;
      }
    },
    series: [
      {
        type: "treemap",
        layoutAlgorithm: "squarified",
        name: dimensionLabel,
        data: treemapData,
        dataLabels: {
          enabled: true,
          style: { fontSize: "11px", fontWeight: "600", color: "#FFFFFF", textOutline: "none" },
          formatter() {
            const entry = entries.find((e) => e.name === this.key);
            return `${truncate(this.key ?? "", 16)}<br/>${fmtPct(entry?.pct ?? 0)}`;
          }
        }
      } as Highcharts.SeriesTreemapOptions
    ]
  };

  return <ReflowingChart options={options} fillHeight />;
};
